import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as go from 'gojs';
import { GojsDiagramColorTheme, GojsDiagramModel, GOJS_COLOR_PALETTE } from '../model';
import { createGojsLayout, hexToRgba, parseSimpleLatexSegments } from './gojs-diagram.util';
import { resolveGojsIcon } from './gojs-icon.util';

const MUTED_LINK_COLOR = '#c9c8c6';

@Component({
  selector: 'dojo-gojs-diagram-viewer',
  standalone: true,
  template: `
    <div class="gojs-diagram-container" [style.height.px]="height() ?? 300">
      <div #diagramDiv class="gojs-diagram-div"></div>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
    .gojs-diagram-container {
      position: relative;
      width: 100%;
      overflow: hidden;
    }
    .gojs-diagram-div {
      width: 100%;
      height: 100%;
      background: transparent;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsDiagramViewerComponent implements AfterViewInit, OnDestroy {
  constructor() {
    effect(() => {
      const model = this.model();
      if (this.diagram && model) {
        this.updateModel(model);
      }
    });
  }

  readonly model = input.required<GojsDiagramModel>();
  readonly layoutType = input<string>('tree');
  readonly height = input<number | undefined>(undefined);
  readonly interactive = input<boolean>(false);
  readonly hoverHighlight = input<boolean>(false);

  @ViewChild('diagramDiv', { static: false }) diagramDiv!: ElementRef<HTMLDivElement>;

  private diagram: go.Diagram | null = null;
  private rafId: number | null = null;

  ngAfterViewInit(): void {
    // Defer initialization to the next animation frame so the container div
    // has its final dimensions. Without this, GoJS may compute layouts with
    // zero width, causing all nodes to pile up at the origin.
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.initDiagram();
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.diagram) {
      this.diagram.div = null;
      this.diagram = null;
    }
  }

  private initDiagram(): void {
    const div = this.diagramDiv?.nativeElement;
    if (!div) {
      return;
    }

    const $ = go.GraphObject.make;
    const isInteractive = this.interactive();
    const hoverHL = this.hoverHighlight();

    this.diagram = $(go.Diagram, div, {
      layout: this.createLayout(),
      isReadOnly: !isInteractive,
      allowSelect: isInteractive,
      allowMove: isInteractive,
      allowDelete: isInteractive,
      allowLink: isInteractive,
      allowRelink: isInteractive,
      autoScale: go.AutoScale.Uniform,
      'animationManager.isEnabled': false,
      'undoManager.isEnabled': isInteractive,
      contentAlignment: go.Spot.Center,
      padding: new go.Margin(10),
    });

    // Hover handlers for label nodes (only active when hoverHighlight is on)
    const nodeHover = hoverHL
      ? {
          mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
            if (obj instanceof go.Node && obj.data?.is_label) {
              this.setLabelHighlight(obj, true);
            }
          },
          mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
            if (obj instanceof go.Node && obj.data?.is_label) {
              this.setLabelHighlight(obj, false);
            }
          },
        }
      : {};

    // Hover handlers for links
    const linkHover = hoverHL
      ? {
          mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
            if (obj instanceof go.Link) {
              this.setLinkHighlight(obj, true);
            }
          },
          mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
            if (obj instanceof go.Link) {
              this.setLinkHighlight(obj, false);
            }
          },
        }
      : {};

    // Node template
    this.diagram.nodeTemplate = $(
      go.Node,
      'Auto',
      {
        locationSpot: go.Spot.Center,
        cursor: 'default',
        ...nodeHover,
      },
      new go.Binding('location', 'loc', go.Point.parse),
      new go.Binding('cursor', 'is_label', (v: boolean) =>
        v && hoverHL ? 'pointer' : isInteractive ? 'pointer' : 'default',
      ),
      $(
        go.Shape,
        {
          figure: 'RoundedRectangle',
          strokeWidth: 0,
          stroke: 'transparent',
          parameter1: 8,
        },
        new go.Binding('figure', 'shape'),
        new go.Binding('fill', 'bg_color'),
        new go.Binding('stroke', 'border_color'),
        new go.Binding('strokeWidth', 'has_border', (b: boolean | number) =>
          b ? (typeof b === 'number' ? b : 1.5) : 0,
        ),
        // Fixed-size shapes (for Venn diagram circles etc.)
        new go.Binding('desiredSize', '', (data: Record<string, unknown>) => {
          if (data['node_width'] && data['node_height']) {
            return new go.Size(data['node_width'] as number, data['node_height'] as number);
          }
          return new go.Size(NaN, NaN);
        }),
      ),
      $(
        go.Panel,
        'Horizontal',
        { margin: new go.Margin(8, 14) },
        // Hide content panel for shape-only nodes (no text, no icon, no latex)
        new go.Binding('visible', '', (data: Record<string, unknown>) => {
          return !!(data['text'] || data['icon_text'] || data['icon_src']);
        }),
        // Emoji icon (rendered as text)
        $(
          go.TextBlock,
          {
            font: '14px Inter, system-ui, sans-serif',
            margin: new go.Margin(0, 4, 0, 0),
            visible: false,
          },
          new go.Binding('text', 'icon_text'),
          new go.Binding('visible', 'icon_text', (t: string) => !!t),
          new go.Binding('font', '', (data: Record<string, unknown>) => {
            const size = (data['font_size'] as number) ?? 14;
            return `500 ${size}px Inter, system-ui, sans-serif`;
          }),
        ),
        // FA / image icon (rendered as picture — FA pre-rendered to PNG via canvas)
        $(
          go.Picture,
          {
            width: 18,
            height: 18,
            margin: new go.Margin(0, 6, 0, 0),
            visible: false,
            imageStretch: go.ImageStretch.Uniform,
          },
          new go.Binding('source', 'icon_src'),
          new go.Binding('visible', 'icon_src', (s: string) => !!s),
        ),
        $(
          go.TextBlock,
          {
            font: '14px Inter, system-ui, sans-serif',
            stroke: '#706f6d',
          },
          new go.Binding('text', 'text'),
          new go.Binding('stroke', 'text_color'),
          new go.Binding('font', '', (data: Record<string, unknown>) => {
            const size = (data['font_size'] as number) ?? 14;
            if (data['is_math']) {
              return `italic ${size}px "Times New Roman", Georgia, serif`;
            }
            const weight = data['font_weight'] === 'bold' ? 'bold' : '500';
            return `${weight} ${size}px Inter, system-ui, sans-serif`;
          }),
        ),
      ),
    );

    // Math/label node template — uses Spot panel so text is never clipped by the shape
    this.diagram.nodeTemplateMap.add(
      'math',
      $(
        go.Node,
        'Spot',
        {
          locationSpot: go.Spot.Center,
          cursor: 'default',
          ...nodeHover,
        },
        new go.Binding('location', 'loc', go.Point.parse),
        new go.Binding('cursor', 'is_label', (v: boolean) =>
          v && hoverHL ? 'pointer' : isInteractive ? 'pointer' : 'default',
        ),
        $(
          go.Shape,
          {
            figure: 'RoundedRectangle',
            strokeWidth: 0,
            stroke: 'transparent',
            parameter1: 8,
          },
          new go.Binding('figure', 'shape'),
          new go.Binding('fill', 'bg_color'),
          new go.Binding('stroke', 'border_color'),
          new go.Binding('strokeWidth', 'has_border', (b: boolean | number) =>
            b ? (typeof b === 'number' ? b : 1.5) : 0,
          ),
          new go.Binding('desiredSize', '', (data: Record<string, unknown>) => {
            if (data['node_width'] && data['node_height']) {
              return new go.Size(data['node_width'] as number, data['node_height'] as number);
            }
            return new go.Size(NaN, NaN);
          }),
        ),
        $(
          go.TextBlock,
          {
            alignment: go.Spot.Center,
            font: 'italic 14px serif',
            stroke: '#706f6d',
          },
          new go.Binding('text', 'text'),
          new go.Binding('stroke', 'text_color'),
          new go.Binding('font', '', (data: Record<string, unknown>) => {
            const size = (data['font_size'] as number) ?? 14;
            return `italic ${size}px serif`;
          }),
        ),
      ),
    );

    // Link template
    this.diagram.linkTemplate = $(
      go.Link,
      {
        routing: go.Routing.Orthogonal,
        corner: 8,
        selectable: isInteractive,
        cursor: hoverHL || isInteractive ? 'pointer' : 'default',
        ...linkHover,
      },
      new go.Binding('routing', 'routing', (r: string) => {
        switch (r) {
          case 'Orthogonal':
            return go.Routing.Orthogonal;
          case 'AvoidsNodes':
            return go.Routing.AvoidsNodes;
          default:
            return go.Routing.Normal;
        }
      }),
      new go.Binding('curve', 'curve', (c: string) => (c === 'Bezier' ? go.Curve.Bezier : go.Curve.None)),
      new go.Binding('curviness', 'curviness'),
      $(
        go.Shape,
        { strokeWidth: 1.5, stroke: '#8a8886' },
        new go.Binding('stroke', 'color'),
        new go.Binding('strokeWidth', 'width'),
      ),
      $(
        go.Shape,
        { toArrow: 'Triangle', strokeWidth: 0, fill: '#8a8886', scale: 0.8 },
        new go.Binding('fill', 'color'),
        new go.Binding('toArrow', 'to_arrow'),
        new go.Binding('visible', 'to_arrow', (a: string) => a !== 'None'),
      ),
    );

    this.updateModel(this.model());
  }

  private createLayout(): go.Layout {
    return createGojsLayout(this.layoutType());
  }

  private resolveColors(theme: GojsDiagramColorTheme | undefined): { bg: string; text: string; border: string } {
    return GOJS_COLOR_PALETTE[theme ?? 'neutral'];
  }

  private updateModel(model: GojsDiagramModel): void {
    if (!this.diagram) {
      return;
    }

    const hoverHL = this.hoverHighlight();
    const neutral = GOJS_COLOR_PALETTE['neutral'];

    const nodeDataArray = model.nodes.map((n) => {
      const colors = this.resolveColors(n.color_theme);
      let iconData: { icon_text: string; icon_src: string } = { icon_text: '', icon_src: '' };
      try {
        iconData = resolveGojsIcon(n.icon);
      } catch {
        // Icon resolution can fail for unknown FA icons — fall back to no icon
      }

      // Direct fill with opacity → RGBA
      let bgColor = colors.bg;
      if (n.fill) {
        bgColor = n.fill_opacity !== undefined && n.fill_opacity < 1 ? hexToRgba(n.fill, n.fill_opacity) : n.fill;
      }

      // Direct stroke overrides
      let borderColor = colors.border;
      let hasBorder: boolean | number = n.border ?? false;
      if (n.stroke) {
        borderColor = n.stroke;
        hasBorder = n.stroke_width ?? 2;
      }

      // Parse LaTeX into native GoJS text with math-like font
      let displayText = n.text ?? '';
      let textColor = colors.text;
      let isMath = false;
      if (n.latex) {
        const parsed = parseSimpleLatexSegments(n.latex);
        displayText = parsed.text;
        if (parsed.color) {
          textColor = parsed.color;
        }
        isMath = true;
      }

      // Detect label nodes (text + border) for hover highlight
      const isLabel = !!(n.text && n.border);
      const origBgColor = bgColor;
      const origTextColor = textColor;
      const origBorderColor = borderColor;

      if (hoverHL && isLabel) {
        bgColor = neutral.bg;
        textColor = neutral.text;
        borderColor = neutral.border;
      }

      return {
        key: n.key,
        text: displayText,
        ...(isMath && { category: 'math' }),
        color_theme: n.color_theme ?? 'neutral',
        bg_color: bgColor,
        text_color: textColor,
        border_color: borderColor,
        has_border: hasBorder,
        icon: n.icon ?? '',
        icon_text: iconData.icon_text,
        icon_src: iconData.icon_src,
        shape: n.shape ?? 'RoundedRectangle',
        font_size: n.font_size,
        font_weight: n.font_weight,
        node_width: n.width,
        node_height: n.height,
        is_math: isMath,
        is_label: isLabel,
        orig_bg_color: origBgColor,
        orig_text_color: origTextColor,
        orig_border_color: origBorderColor,
        ...(n.loc && { loc: n.loc }),
      };
    });

    const linkDataArray = model.links.map((l) => {
      const origColor = l.color ?? '#8a8886';
      return {
        from: l.from,
        to: l.to,
        color: hoverHL ? MUTED_LINK_COLOR : origColor,
        orig_color: origColor,
        width: l.width ?? 1.5,
        to_arrow: l.to_arrow ?? 'Triangle',
        routing: l.routing ?? 'Orthogonal',
        curve: l.curve ?? 'None',
        curviness: l.curviness ?? NaN,
      };
    });

    this.diagram.model = new go.GraphLinksModel(nodeDataArray, linkDataArray);
  }

  /** Highlight or dim a label node and all its connected links. */
  private setLabelHighlight(node: go.Node, highlight: boolean): void {
    const diagram = node.diagram;
    if (!diagram) {
      return;
    }
    const model = diagram.model;
    const data = node.data;
    const neutral = GOJS_COLOR_PALETTE['neutral'];

    diagram.startTransaction('hover');
    model.setDataProperty(data, 'bg_color', highlight ? data.orig_bg_color : neutral.bg);
    model.setDataProperty(data, 'text_color', highlight ? data.orig_text_color : neutral.text);
    model.setDataProperty(data, 'border_color', highlight ? data.orig_border_color : neutral.border);
    node.linksConnected.each((link) => {
      model.setDataProperty(link.data, 'color', highlight ? link.data.orig_color : MUTED_LINK_COLOR);
    });
    diagram.commitTransaction('hover');
  }

  /** Highlight or dim a link and its source label node. */
  private setLinkHighlight(link: go.Link, highlight: boolean): void {
    const diagram = link.diagram;
    if (!diagram) {
      return;
    }
    const model = diagram.model;
    const neutral = GOJS_COLOR_PALETTE['neutral'];

    diagram.startTransaction('hover');
    model.setDataProperty(link.data, 'color', highlight ? link.data.orig_color : MUTED_LINK_COLOR);
    const fromNode = link.fromNode;
    if (fromNode?.data?.is_label) {
      model.setDataProperty(fromNode.data, 'bg_color', highlight ? fromNode.data.orig_bg_color : neutral.bg);
      model.setDataProperty(fromNode.data, 'text_color', highlight ? fromNode.data.orig_text_color : neutral.text);
      model.setDataProperty(
        fromNode.data,
        'border_color',
        highlight ? fromNode.data.orig_border_color : neutral.border,
      );
    }
    diagram.commitTransaction('hover');
  }
}
